import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { createElement } from 'commonAssets/lib/createElement.js';
import { modal } from 'commonAssets/lib/modal.js';
import { clearMessage } from 'commonAssets/lib/clearMessage.js';
import { loadingGif } from 'commonAssets/lib/loadingGif.js';
import { validateField } from 'commonAssets/lib/validateField.js';
import { toggleFieldError } from 'commonAssets/lib/toggleFieldError.js';
import { uniformClaimDeadline } from 'commonAssets/lib/uniformClaimDeadline.js';
import { ConfirmEmailAddress } from 'commonAssets/lib/ConfirmEmailAddress.js';
import { localDatetime } from 'commonAssets/lib/localDatetime.js';
import { i18n, loading, outputDiv, reCAPTCHAv3 } from 'commonAssets/js/constants';
import { appPayMethod } from 'commonAssets/js/constants';
import { dstProcess } from 'commonAssets/components/FormBase/DigitalDisbursements';

export class CustomSubmitClaim extends SubmitClaim {
    constructor({ appClaimForm, userLanguage, customInitClaim, customProcessClaim, customLoadMember, customDstProcess, errorHandler }) {
        super(appClaimForm, userLanguage, customInitClaim, customProcessClaim, customLoadMember, customDstProcess, errorHandler);
    }

    /**
     * Override init_claim to gate access by cm_data.ClassMemberStatus.
     * Only active `Deficient` status with `CanEdit` is allowed past the guard.
     * Fails closed when ClassMemberStatus is missing or not an array.
     */
    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                if (result.data.error) {
                    this.setup_result_data_error(result);
                    loadingGif(loading, "hide", 250);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                if (form.form_options.notice_confirmation.display === true) {
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id = document.getElementById("zero_zero_id");
                    const confirmation_code = document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Deficiency portal: never allow the skip-guard bypass — the
                    // cm_data gate only runs in the guard's submit handler.
                    if (skip_guard) skip_guard.remove();

                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document.getElementById("guard").classList.add("was-validated");
                        });

                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            [zero_zero_id, confirmation_code].forEach(function (input) {
                                valid = validateField(input) && valid;
                            });
                            if (!valid) return;

                            loadingGif(loading, "show");

                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    if (result.data.error) {
                                        toggleFieldError(zero_zero_id, true, "");
                                        toggleFieldError(confirmation_code, true, "");
                                        this.errorHandler.log_exception(
                                            new Error("Error loading class member", { cause: result })
                                        );
                                        this.errorHandler.display_modal(result);
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    this.claim.data.zero_zero_id = member.zero_zero_id;
                                    this.claim.data.confirmation_code = member.confirmation_code;

                                    // Deficiency gate: allow only active Deficient + CanEdit
                                    const classMemberStatus = Boolean(result.data.claim_id)
                                        ? result.data.class_member?.cm_data?.ClassMemberStatus
                                        : result.data.cm_data?.ClassMemberStatus;

                                    if (!Array.isArray(classMemberStatus)) {
                                        modal(
                                            i18n.getTranslation("error"),
                                            "This portal is not available for new claims."
                                        );
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    let cmLabel = false;
                                    let cmActive = false;
                                    let cmCanEdit = false;
                                    for (const status of classMemberStatus) {
                                        if (status.Label !== "Deficient") continue;

                                        if (status.Deadline) {
                                            const deadlineDate = new Date(status.Deadline);
                                            // Fail closed on unparseable Deadline values, but report them distinctly.
                                            if (Number.isNaN(deadlineDate.getTime())) {
                                                this.errorHandler.log_exception(
                                                    new Error("Unparseable class member deficiency deadline", { cause: status.Deadline })
                                                );
                                                modal(
                                                    i18n.getTranslation("error"),
                                                    "Unable to verify the deadline for this claim. Please contact the Settlement Administrator."
                                                );
                                                loadingGif(loading, "hide", 250);
                                                return;
                                            }
                                            if (new Date() > deadlineDate) {
                                                modal(
                                                    i18n.getTranslation("error"),
                                                    "The deadline to fix this claim has passed."
                                                );
                                                loadingGif(loading, "hide", 250);
                                                return;
                                            }
                                        }

                                        cmLabel = true;
                                        cmActive = Boolean(status.Active);
                                        cmCanEdit = Boolean(status.CanEdit);
                                        break;
                                    }

                                    if (!cmLabel || !cmActive || !cmCanEdit) {
                                        modal(
                                            i18n.getTranslation("error"),
                                            "This portal is not available for claim edits."
                                        );
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    const environment = this.claim.data.domain.split(".")[0];

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id = result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(form, container, result.data, claim_deadline);
                                            return;
                                        }
                                    }

                                    this.populate_claim_form(form, container, result.data, claim_deadline);
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error("Error loading class member", { cause: error })
                                    );
                                    this.errorHandler.display_modal(error);
                                    loadingGif(loading, "hide", 250);
                                });
                        });
                } else {
                    this.edit_claim(form);

                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    populate_claim_form(form, container, class_member, claim_deadline) {
        // set Google customer ID
        this.claim.data.google_customer_id = class_member.google_customer_id;
        super.populate_claim_form(form, container, class_member, claim_deadline);
    }

    process_claim(e, claim, deadline) {
        console.log("[process_claim]")

        /**
         * Prevent the default form submission.
         * 
         * @var {Event} e - The submit event object.
         * @returns {void}
         */
        e.preventDefault();

        /**
         * Disable the submit button to prevent multiple submissions.
         * 
         * Since: 2023.09.25
         * 
         * @returns {void}
         */
        document.getElementById('submit-claim').disabled = true;

        // var claim = this.claim;
        if (claim.flags.sent) {
            return;
        }

        clearMessage();

        claim.flags.valid = true;
        claim.data.fields = {};

        this.init_claim_inputs();

        this.format_claim_data(claim);

        claim.data.contact.date = localDatetime();

        if (document.getElementById('documentation')) {
            this.process_claim_files(this.claim);
        }

        if (document.getElementById('email_address') && document.getElementById('confirm_email_address')) {
            ConfirmEmailAddress('process', this.claim);
        }

        // if appPayMethod is defined and not empty, we run dstProcess from FormBase/DigitalDisbursements.js
        if (appPayMethod && appPayMethod !== "" && this.customDstProcess === null) {
            claim = dstProcess(this.claim);
        }
        else if (appPayMethod && appPayMethod !== "" && this.customDstProcess !== null) {
            claim = this.customDstProcess(this.claim);
        }

        /**
         * Define the environment for the s3 uploader.
         */
        let s3env = this.config.url;

        // Custom process claim
        this.customProcessClaim(this.claim).then((claim) => {
            if (claim.flags.valid) {
                grecaptcha.enterprise.execute(reCAPTCHAv3, { action: 'submit' }).then((token) => {
                    claim.data.token = token;
                    this.post_claim(claim, s3env);
                });
            } else {
                document.getElementById('submit-claim').disabled = false;
                modal(i18n.getTranslation('error'), i18n.getTranslation('claim_form_errors'));
            }
        });
    }
}
